import { X, User, Star, MapPin, Briefcase, MessageCircle, Phone, CheckCircle, Shield, Users } from 'lucide-react';
import { ServiceProvider } from '@/types';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';

interface ProviderDetailsModalProps {
    provider: ServiceProvider;
    onClose: () => void;
    onMessage: (providerId: string, providerName: string, providerBusinessName: string, initialMessage?: string) => void;
}

export const ProviderDetailsModal: React.FC<ProviderDetailsModalProps> = ({
    provider,
    onClose,
    onMessage
}) => {
    const handleMessageClick = () => {
        // Navigate directly to chat page (no message input modal)
        // The user can type the message in the chat page itself
        onMessage(provider.id, provider.businessName, provider.businessName);
        onClose();
    };

    const formatDate = (value?: string | null) => {
        if (!value) return '—';
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return '—';
        return parsed.toLocaleDateString();
    };

    return (
        // Provider Details Modal
        <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4 transition-all animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative animate-in zoom-in-95 duration-200">

                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-20 rounded-t-2xl">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        {provider.businessName}
                        {provider.verified && (
                            <Badge variant="success" size="sm" className="ml-2">
                                <Shield className="w-3 h-3 mr-1" />
                                Verified
                            </Badge>
                        )}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-8 space-y-8">
                    {/* Profile Summary */}
                    <div className="flex flex-col md:flex-row items-start gap-6">
                        <div className="w-24 h-24 bg-gray-100 rounded-xl flex items-center justify-center border border-gray-200 flex-shrink-0">
                            {provider.profileImage ? (
                                <img src={provider.profileImage} alt={provider.businessName} className="w-full h-full object-cover rounded-xl" />
                            ) : (
                                <User className="w-10 h-10 text-gray-400" />
                            )}
                        </div>
                        <div className="flex-1">
                            <p className="text-gray-600 text-sm leading-relaxed mb-4">
                                {provider.bio || "No bio available."}
                            </p>

                            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                                <div className="flex items-center bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100">
                                    <Star className="w-4 h-4 text-amber-400 fill-current mr-2" />
                                    <span className="font-semibold text-gray-900 mr-1">{provider.rating}</span>
                                    <span className="text-gray-400">({provider.reviewCount} reviews)</span>
                                </div>
                                <div className="flex items-center bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100">
                                    <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                                    <span>{provider.location}</span>
                                </div>
                                <div className="flex items-center bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100">
                                    <Briefcase className="w-4 h-4 mr-2 text-gray-400" />
                                    <span>{provider.completedJobs} Jobs Completed</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {/* Trust & Verification */}
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                            <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                                <Shield className="w-4 h-4 mr-2 text-primary-600" />
                                Trust & Verification
                            </h4>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between items-center pb-2 border-b border-gray-200/50">
                                    <span className="text-gray-600">Public Liability Insurance</span>
                                    <span className={provider.insurance?.hasInsurance ? 'text-emerald-600 font-medium flex items-center' : 'text-gray-400'}>
                                        {provider.insurance?.hasInsurance ? (
                                            <><CheckCircle className="w-3 h-3 mr-1" /> Valid (€2M)</>
                                        ) : 'Not Verified'}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center pb-2 border-b border-gray-200/50">
                                    <span className="text-gray-600">Background Check</span>
                                    <span className={provider.backgroundCheck ? 'text-emerald-600 font-medium flex items-center' : 'text-gray-400'}>
                                        {provider.backgroundCheck ? (
                                            <><CheckCircle className="w-3 h-3 mr-1" /> Checked</>
                                        ) : 'Pending'}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Experience</span>
                                    <span className="font-medium text-gray-900">{provider.yearsOfExperience} Years</span>
                                </div>
                            </div>
                        </div>

                        {/* Availability & Response */}
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                            <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                                <MessageCircle className="w-4 h-4 mr-2 text-primary-600" />
                                Availability
                            </h4>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between items-center pb-2 border-b border-gray-200/50">
                                    <span className="text-gray-600">Response Time</span>
                                    <span className="font-medium text-gray-900">{provider.responseTime || 'Within 24h'}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Emergency Services</span>
                                    <span className={provider.availability?.emergencyService ? 'text-emerald-600 font-medium' : 'text-gray-400'}>
                                        {provider.availability?.emergencyService ? 'Available 24/7' : 'Not Available'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {provider.employmentHistory && provider.employmentHistory.length > 0 && (
                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                            <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                                <Users className="w-4 h-4 mr-2 text-primary-600" />
                                Employment History
                            </h4>
                            <div className="space-y-3 text-sm">
                                {provider.employmentHistory.map((item, index) => (
                                    <div key={`${item.companyId}-${index}`} className="border-b border-gray-200/60 pb-2 last:border-b-0 last:pb-0">
                                        <div className="font-medium text-gray-900">{item.companyName || 'Company'}</div>
                                        <div className="text-gray-600">Role: {item.role || 'Member'}</div>
                                        <div className="text-gray-600">
                                            {formatDate(item.startDate)} to {item.endDate ? formatDate(item.endDate) : 'Present'}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-100">
                        <Button
                            variant="primary"
                            fullWidth
                            onClick={handleMessageClick}
                            icon={<MessageCircle className="w-4 h-4" />}
                        >
                            Message Provider
                        </Button>
                        <Button
                            variant="outline"
                            fullWidth
                            icon={<Phone className="w-4 h-4" />}
                            onClick={() => window.location.href = `tel:${provider.id}`} // Mock phone action
                        >
                            Call Now
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};
